import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {FilesCleanUpConfigType, ExecutionsPaginationType} from 'src/types';
import {Execution} from 'src/types/models/Execution';
import {useExecutionService} from 'src/services';
import {Badge, Paper, Table, Text} from '@mantine/core';
import useTranslation from 'src/i18next.config';

import { authActions } from 'src/store/reducers/auth-reducer';
import {formatDate} from 'src/helpers/format-date';
import {EFilesType} from 'src/types/enum/files-type.enum';
import {ELocalesFormats} from 'src/types/enum/locales-formats.enum';
import {ERequestActions} from 'src/types/enum/request-actions.enum';
import {ExecutionStatusBadge} from './ExecutionStatusBadge';
import {ExecutionDownloadButton} from './ExecutionDownloadButton';
import {ExecutionActionMenu} from './ExecutionActionMenu';
import {IconSquareRoundedX, IconTrash, IconCopy} from '@tabler/icons-react';
import {userProfileActions} from 'src/store/reducers/user-reducer';
import {ExecutionPagination} from './ExecutionPagination';
import ExecutionsSearch from './ExecutionsSearch';
import ExecutionsSearchQs from 'src/types/interfaces/executions-search-sq';
import {showNotification} from '@mantine/notifications';
import { ERequestStatus } from 'src/types/enum/request-status.enum';

const INITIAL_PAGE = 1;
const INITIAL_PAGE_LIMIT = 9;

function formatDuration(seconds?: number): string {
    if (seconds == null || seconds <= 0) return '-';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

export function ExecutionsList() {

    const executionService = useExecutionService();
    const {t} = useTranslation();
    const token = authActions.useToken();
    const navigate = useNavigate();

    const [executions, setExecutions] = useState<Execution[]>([]);
    const [executionsPagination, setExecutionsPagination] = useState<ExecutionsPaginationType>();
    const [filesCleanUpConfig, setFilesCleanUpConfig] = useState<FilesCleanUpConfigType>();
    const [runningCount, setRunningCount] = useState<number | null>(null);
    const [currentFilters, setCurrentFilters] = useState<ExecutionsSearchQs>({});
    const userProfile = userProfileActions.useUserProfile();

    const showUserColumns = userProfile.isAdmin || userProfile.isOrganizationManager;
    const showOrgColumn = userProfile.isAdmin;
    const showExecutorColumn = userProfile.isAdmin || userProfile.isOrganizationManager;

    useEffect(function () {
        if (executions.length < 1) {
            fetchExecutions(INITIAL_PAGE, {});
        }
        fetchRunningCount();
    }, []);

    function fetchRunningCount() {
        executionService.getRunningCount({
            success: (res: { runningCount: number }) => setRunningCount(res.runningCount),
            error: () => { /* no-op */ },
        });
    }

    function cancelExecutionClicked(executionId: string) {
        executionService.postStopExecution(
            {
                error: () => {
                    showNotification({
                        title: t('errorCancelingExecution'),
                        message: t('anErrorOccurredPleaseRetryLater'),
                        color: 'red',
                    });
                },
                success: () => {
                    showNotification({
                        title: t('stopExecutionRequestSent'),
                        message: t('stopExecutionRequestSent'),
                        color: 'green',
                    });
                    const newExecutions = executions.map(exec => {
                        if (exec._id === executionId) {
                            exec.status = ERequestStatus.CANCELED;
                        }
                        return exec;
                    });
                    setExecutions(newExecutions);
                    fetchRunningCount();
                },
            },
            {executionId}
        );
    }

    function downloadClicked(id: string, index = 0, source: string) {
        executionService.getResultsFiles(
            {
                error: () => {
                    showNotification({
                        title: t('errorFetchingData'),
                        message: t('anErrorOccurredPleaseRetryLater'),
                        color: 'red',
                    });
                },
                success: res => {
                    const data = res.data;
                    if (data === -1 || data === '-1') {
                        return;
                    } else {
                        const blob = new Blob([data]);
                        const link = document.createElement('a');
                        link.href = window.URL.createObjectURL(blob);
                        const filename = res.headers.get('content-disposition')?.split(';')[1]?.split('=')[1];
                        if (filename) {
                            link.download = JSON.parse(filename) || 'results';
                            link.click();
                            link.remove();
                            link.parentElement?.removeChild(link);
                            downloadClicked(id, index + 1, source);
                        }
                    }
                },
            },
            {executionId: id, index: index, source}
        );
    }

    function deleteFilesClicked(executionId: string) {
        executionService.postDeleteExecutionFiles(
            {
                error: () => {
                    showNotification({
                        title: t('errorDeletingExecutionFiles'),
                        message: t('anErrorOccurredPleaseRetryLater'),
                        color: 'red',
                    });
                },
                success: () => {
                    showNotification({
                        title: t('deleteFilesRequestSent'),
                        message: t('deleteFilesRequestSent'),
                        color: 'green',
                    });
                },
            },
            {executionId}
        );
    }

    async function fetchExecutions(page: number, filters: ExecutionsSearchQs) {
        try {
            const params = new URLSearchParams();
            params.set('page', page.toString());
            params.set('limit', (filters.limit || INITIAL_PAGE_LIMIT).toString());
            if (filters.organization) params.set('organization', filters.organization);
            if (filters.orgName) params.set('orgName', filters.orgName);
            if (filters.user) params.set('user', filters.user);
            if (filters.userEmail) params.set('userEmail', filters.userEmail);
            if (filters.status) params.set('status', filters.status);
            if (filters.doneDate) params.set('doneDate', filters.doneDate.toString());
            if (filters.runningDate) params.set('runningDate', filters.runningDate.toString());
            if (filters.executionRequestId) params.set('executionRequestId', filters.executionRequestId);

            const response = await fetch(`${process.env.REACT_APP_API_URL}/request?${params}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            const res = await response.json();
            setExecutions(res.executions.docs);
            setFilesCleanUpConfig(res.config);
            setExecutionsPagination({...res.executions, docs: null});
            fetchRunningCount();
        } catch (error) {
            console.error(error);
            showNotification({
                title: t('errorFetchingData'),
                message: t('executions.errorFethingExecutions'),
                color: 'red',
            });
        }
    }

    function getExecutionRows(executions: Execution[]) {
        return executions.map((exec: Execution) => {
            let creationDate: string | null = null;
            let runningDate: string | null = null;
            let doneDate: string | null = null;
            if (exec.creationDate) {
                creationDate = formatDate(new Date(exec.creationDate), ELocalesFormats.FR);
            }
            if (exec.runningDate) {
                runningDate = formatDate(new Date(exec.runningDate), ELocalesFormats.FR);
            }
            if (exec.doneDate) {
                doneDate = formatDate(new Date(exec.doneDate), ELocalesFormats.FR);
            }

            const executionActions = [
                {
                    type: 'copy-id',
                    isDisabled: false,
                    label: 'Copy ID',
                    icon: <IconCopy size="1rem" color="#228be6" stroke={1.5} />,
                    onClick: (event: any, executionRequestId: string) => {
                        navigator.clipboard.writeText(executionRequestId).then(() => {
                            showNotification({
                                title: 'Copied to clipboard',
                                message: 'Execution ID copied to clipboard',
                                color: 'green',
                            });
                        });
                    },
                },
                {
                    type: ERequestActions.CANCEL,
                    isDisabled: !exec.canCancelExecution,
                    label: t('executions.cancelExecution'),
                    icon: <IconSquareRoundedX size="1rem" color="#fd7e14" stroke={1.5} />,
                    onClick: (event: any, executionRequestId: string) => cancelExecutionClicked(executionRequestId),
                },
                {
                    type: ERequestActions.DELETE_FILES,
                    isDisabled: !exec.canDeleteFiles,
                    label: t('executions.deleteFiles'),
                    icon: <IconTrash size="1rem" color="#fa5252" stroke={1.5} />,
                    onClick: (event: any, executionRequestId: string) => deleteFilesClicked(executionRequestId),
                },
            ];

            function getCleanUpDate(
                doneDate: number | null,
                downloadDate: number | null,
                daysBeforeDeletionForDownload: number | undefined
            ): number | undefined {
                const msBeforeCleanUpForDone =
                    1000 *
                    60 *
                    60 *
                    24 *
                    (filesCleanUpConfig?.daysBeforeDeletionForDone ? filesCleanUpConfig.daysBeforeDeletionForDone : 0);
                const msBeforeCleanUpForDownload =
                    1000 * 60 * 60 * 24 * (daysBeforeDeletionForDownload ? daysBeforeDeletionForDownload : 0);

                if (!doneDate && !downloadDate) {
                    return undefined;
                }
                if (downloadDate && doneDate) {
                    if (downloadDate < doneDate) {
                        return downloadDate + msBeforeCleanUpForDone;
                    } else {
                        return doneDate + msBeforeCleanUpForDownload;
                    }
                } else {
                    if (downloadDate) {
                        return downloadDate + msBeforeCleanUpForDownload;
                    }
                    if (doneDate) {
                        return doneDate + msBeforeCleanUpForDone;
                    }
                }
            }

            function getDownloadLabel(execution: Execution, filesType: string): string {
                let execFilesInfo;
                if (filesType === EFilesType.UPLOADS) {
                    execFilesInfo = {
                        numberOfFiles: execution.numberOfUploadedFiles,
                        hasDeletedFiles: execution.hasDeletedUploadFiles,
                    };
                } else {
                    execFilesInfo = {
                        numberOfFiles: execution.numberOfResultedFiles,
                        hasDeletedFiles: execution.hasDeletedResultFiles,
                    };
                }

                if (execFilesInfo.hasDeletedFiles) {
                    return t('cleanupConfiguration.filesDeleted');
                }
                if (!execFilesInfo.numberOfFiles || execFilesInfo.numberOfFiles < 1) {
                    return t('cleanupConfiguration.noFiles');
                }

                return '';
            }

            return (
                <Table.Tr
                    key={exec._id}
                    onClick={() => navigate(`/executions/${exec._id}`)}
                    style={{cursor: 'pointer'}}
                >
                    <Table.Td>
                        <Text fw={600} size="sm">{exec.name}</Text>
                        <Text size="xs" c="dimmed">{exec.label}</Text>
                        <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>ID: {exec._id}</Text>
                    </Table.Td>
                    <Table.Td>
                        <ExecutionStatusBadge status={exec.status} errorMessage={exec.errorMessage} />
                    </Table.Td>
                    {showUserColumns && (
                        <Table.Td>
                            <Text size="sm">{exec.user ? `${exec.user.firstName} ${exec.user.lastName}` : ''}</Text>
                            <Text size="xs" c="dimmed">{exec.user ? exec.user.email : ''}</Text>
                        </Table.Td>
                    )}
                    {showOrgColumn && (
                        <Table.Td>
                            <Text size="sm">{exec.organization?.name ?? ''}</Text>
                        </Table.Td>
                    )}
                    <Table.Td style={{fontSize: '0.8rem'}}>
                        <div>{creationDate ? `Created: ${creationDate}` : ''}</div>
                        <div>{runningDate ? `Running: ${runningDate}` : ''}</div>
                        <div>{doneDate ? `Done: ${doneDate}` : ''}</div>
                    </Table.Td>
                    <Table.Td>
                        <Text size="sm">{formatDuration(exec.durationSeconds)}</Text>
                    </Table.Td>
                    {showExecutorColumn && (
                        <Table.Td>
                            <Text size="xs">{exec.agentReference ?? '-'}</Text>
                        </Table.Td>
                    )}
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                        <ExecutionDownloadButton
                            label={getDownloadLabel(exec, EFilesType.UPLOADS)}
                            nbFiles={exec.numberOfUploadedFiles}
                            size={exec.weightOfUploadedFiles}
                            isDisabled={!exec.canDownloadUploadFiles}
                            showMetadata={exec.showUploadedFilesMetadata}
                            cleanUpDate={getCleanUpDate(
                                exec.doneDate,
                                exec.downloadUploadedFilesDate,
                                filesCleanUpConfig?.daysBeforeDeletionForDownloadUploadedFiles
                            )}
                            onClick={e => {
                                e.preventDefault();
                                downloadClicked(exec._id, 0, EFilesType.UPLOADS);
                            }}
                        />
                        <div style={{marginTop: 4}} />
                        <ExecutionDownloadButton
                            label={getDownloadLabel(exec, EFilesType.RESULTS)}
                            nbFiles={exec.numberOfResultedFiles}
                            size={exec.weightOfResultedFiles}
                            isDisabled={!exec.canDownloadResultFiles}
                            showMetadata={exec.showResultedFilesMetadata}
                            cleanUpDate={getCleanUpDate(
                                exec.doneDate,
                                exec.downloadResultedFilesDate,
                                filesCleanUpConfig?.daysBeforeDeletionForDownloadResultedFiles
                            )}
                            onClick={e => {
                                e.preventDefault();
                                downloadClicked(exec._id, 0, EFilesType.RESULTS);
                            }}
                        />
                    </Table.Td>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                        <ExecutionActionMenu execution={exec} executionActions={executionActions} />
                    </Table.Td>
                </Table.Tr>
            );
        });
    }

    async function onPaginationChange(page: number) {
        await fetchExecutions(page, currentFilters);
    }

    async function searchExecutions(search: ExecutionsSearchQs) {
        setCurrentFilters(search);
        await fetchExecutions(INITIAL_PAGE, search);
    }

    return (
        <>
            <Paper shadow="md" style={{width: 'fit-content', margin: 'auto'}}>
                {runningCount !== null && runningCount > 0 && (
                    <div style={{padding: '0.5rem 1rem 0'}}>
                        <Badge color="cyan" size="lg" variant="filled">
                            {runningCount} {userProfile.isAdmin ? 'running system-wide' : 'running'}
                        </Badge>
                    </div>
                )}
                <ExecutionsSearch onSearch={searchExecutions} />
                <Table.ScrollContainer minWidth={800}>
                    <Table highlightOnHover>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th style={{minWidth: '160px'}}>{'Execution'}</Table.Th>
                                <Table.Th>{t('status')}</Table.Th>
                                {showUserColumns && (
                                    <Table.Th style={{minWidth: '180px'}}>{t('userName')}</Table.Th>
                                )}
                                {showOrgColumn && (
                                    <Table.Th style={{minWidth: '140px'}}>{'Organization'}</Table.Th>
                                )}
                                <Table.Th style={{minWidth: '135px'}}>{t('dates') || 'Dates'}</Table.Th>
                                <Table.Th>{'Duration'}</Table.Th>
                                {showExecutorColumn && (
                                    <Table.Th>{'Executor'}</Table.Th>
                                )}
                                <Table.Th style={{minWidth: '100px'}}>{t('files') || 'Files'}</Table.Th>
                                <Table.Th>{t('executions.label') || 'Actions'}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>{executions && getExecutionRows(executions)}</Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
                {executionsPagination && (
                    <ExecutionPagination
                        executionsPagination={executionsPagination}
                        onPaginationChange={onPaginationChange}
                    />
                )}
            </Paper>
        </>
    );
}

export default ExecutionsList;
